import { inOptimizelyExperiment } from '@zola-helpers/client';

import { getInvitesCardState } from '../selectors/dashboard/InvitesCard';
import { getSaveTheDateCardState } from '../selectors/dashboard/SaveTheDateCard';
import { getCookie, getAllStorage, setAllStorage } from './storage';
import { userCreatedAfter } from './trackingHelper';
import {
  extendedWebsiteOnboardOptimizelyConfig3,
  zolaStoriesConfig,
  planningDashboardSecondaryNavConfig
} from './optimizelyConfigs';

export const isInExtendedWebsiteOnboardTest = ({ shouldTriggerExperiment = false } = {}) => inOptimizelyExperiment(extendedWebsiteOnboardOptimizelyConfig3, shouldTriggerExperiment);

export const isInZolaStoriesTest = ({ shouldTriggerExperiment = false } = {}) => inOptimizelyExperiment(zolaStoriesConfig, shouldTriggerExperiment);

export const isInPlanningDashboardSecondaryNavTest = ({ shouldTriggerExperiment = false } = {}) => inOptimizelyExperiment(planningDashboardSecondaryNavConfig, shouldTriggerExperiment);

export const userHasNoRegistryOrCards = (store) => {
  const state = store.getState();
  const invitesCardState = getInvitesCardState(state);
  const saveTheDateCardState = getSaveTheDateCardState(state);
  const invitationsActivated = invitesCardState.activated || saveTheDateCardState.activated;
  const userContext = state && state.user && state.user.userContext;
  const hasRegistry = (userContext && userContext.registry_ids && userContext.registry_ids.length > 0) || false;
  return !(hasRegistry || invitationsActivated);
};

// Function for determining if a new user* should go to the experiment page component for a test.
//
// * New user defined as a user that doesn't have an activated registry, has no drafts, and
// has never see the wedding dashboard before the test began.
//
// Example params:
// const newDashboardTestProps = {
//   isOptimizelyTestSkipped: true,
//   devStart: '2019-01-25T16:00:00.000Z', // Dev - Friday at 11am EST, January 25th, 2019
//   prodStart: '2019-02-04T21:45:37.103Z', // Prod - Monday at 2:30pm EST, January 28th, 2019
//   cookieName: 'isNewUserWeddingPlanningDashboard',
//   optimizelyConfig: newWeddingPlanningDashConfig,
//   store,
// };

export const shouldGoToTestPage = ({ isOptimizelyTestSkipped, devStart, prodStart, cookieName, optimizelyConfig, store }) => {
  let isUserInTest = window.zola.env === 'development';
  const useOldTestDateForWeddingDash = window.zola.env === 'development' || window.zola.env === 'production';
  const testBeganAtStr = useOldTestDateForWeddingDash ? devStart : prodStart; // Dates are in ISO 8601 format E.G. 2019-02-04T19:10:37.103Z
  const userCreatedAfterTestBegan = userCreatedAfter(testBeganAtStr);
  const wasAlreadyNewUser = getAllStorage(cookieName) === 'true';
  const isNewUser = wasAlreadyNewUser || (userCreatedAfterTestBegan && userHasNoRegistryOrCards(store));

  if (!wasAlreadyNewUser && isNewUser) {
    setAllStorage(cookieName, 'true');
  }

  if (!isOptimizelyTestSkipped && isNewUser) {
    isUserInTest = inOptimizelyExperiment(optimizelyConfig);
  }

  return isUserInTest;
};

export default inOptimizelyExperiment;

// TODO: Write tests
// Returns the optimizely variation id for a given experiment, undefined if not in the experiment.
export function getOptimizelyVariationId(experimentId) {
  // Skip for bots
  const automatedTest = getCookie('automatedTest');
  if (automatedTest) {
    return false;
  }

  // Check if optimizely installed
  if (window.optimizely && window.optimizely.get) {
    const state = window.optimizely.get('state');
    const allExperiments = state.getExperimentStates({ isActive: true });
    const experiment = allExperiments[experimentId];

    if (experiment && experiment.variation) {
      return experiment.variation.id;
    }
    // TODO: What if a user is in the exlusion group?
  }
  return undefined;
}

// TODO: Write tests
// Puts a user into an experiment and returns the variation (or control) the user is bucketed into.
export function triggerExperimentAndGetVariationId(optimizelyConfig) {
  // Skip for bots
  const automatedTest = getCookie('automatedTest');
  if (automatedTest) {
    return false;
  }
  // Check if optimizely installed
  if (window.optimizely && window.optimizely.get) {
    const optimizelyData = window.optimizely.get('data');
    const {
      pageName,
      experimentId,
    } = optimizelyConfig[optimizelyData.projectId];

    /*
      This is important! Since we're manually triggering when a user should be placed in the experiment,
      it is the developer's responsibility to indicate that the user should be included.
      Otherwise, we run the risk of placing users in experiments incorrectly.
      Sometimes you might want to determine if a user is in an variation at a point where you would not
      want to trigger an experiment, for those cases use (getOptimizelyVariationId), not this method.
    */
    window.optimizely = window.optimizely || [];
    window.optimizely.push({
      type: 'page',
      pageName
    });

    const state = window.optimizely.get('state');
    const allExperiments = state.getExperimentStates();
    const experiment = allExperiments[experimentId];

    if (experiment && experiment.variation) {
      return experiment.variation.id;
    }
  }
  return undefined;
}

export function checkUserInExperiment(experimentId) {
  // Check if optimizely installed
  if (window.optimizely && window.optimizely.get) {
    const state = window.optimizely.get('state');
    const allExperiments = state.getExperimentStates();
    const experiment = allExperiments[experimentId];
    return experiment;
  }
  return undefined;
}

export function getExperimentData(optimizelyConfig) {
  if (window.optimizely && window.optimizely.get) {
    const optimizelyData = window.optimizely.get('data');
    const {
      pageName,
      experimentId,
      variationId,
    } = optimizelyConfig[optimizelyData.projectId];
    return {
      pageName,
      experimentId,
      variationId,
    };
  }
  return {
    pageName: undefined,
    experimentId: undefined,
    variationId: undefined,
  };
}



// WEBPACK FOOTER //
// ./src/util/optimizelyHelper.js